import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BankCsvImportPanel from '../BankCsvImportPanel';
import PayPalImportPanel from '../PayPalImportPanel';
import UpgradeNotice from '../../shared/UpgradeNotice';
import AppDatePicker from '../../shared/AppDatePicker';
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
    resolveCustomerOption,
} from '../../shared/customerSelectUtils';
import {
    SUPPLIER_SELECT_EMPTY_HELPER,
    SUPPLIER_SELECT_LABEL,
    SUPPLIER_SELECT_LOADING,
    SUPPLIER_SELECT_NO_OPTIONS,
    SUPPLIER_SELECT_PLACEHOLDER,
    getSupplierOptionLabel,
    isSupplierOptionEqualToValue,
    resolveSupplierOption,
} from '../../shared/supplierSelectUtils';
import {
    ALLOCATION_TYPES,
    formatAmount,
    formatDate,
    formatDateTime,
    renderTargetOptions,
} from './unassignedTransactionsUtils';

export default function UnassignedTransactionsView({
    hasAccounting,
    loading,
    bankLoading,
    error,
    bankError,
    transactions,
    bankTransactions,
    fetchTransactions,
    assignDialogOpen,
    bankAssignDialogOpen,
    activeTransaction,
    activeBankTransaction,
    allocationRows,
    allocationErrors,
    bankAllocationRows,
    bankAllocationErrors,
    bankComment,
    setBankComment,
    assigning,
    bankAssigning,
    deletingTransactionId,
    deletingBankTransactionId,
    assignError,
    bankAssignError,
    referenceLoading,
    referenceData,
    customerOptions,
    supplierOptions,
    creatingCustomer,
    creatingSupplier,
    bankCreatingCustomer,
    bankCreatingSupplier,
    openAssignDialog,
    openBankAssignDialog,
    closeAssignDialog,
    closeBankAssignDialog,
    updateAllocationRow,
    updateAllocationNested,
    updateBankAllocationRow,
    updateBankAllocationNested,
    addAllocationRow,
    removeAllocationRow,
    addBankAllocationRow,
    removeBankAllocationRow,
    handleAllocationTypeChange,
    handleBankAllocationTypeChange,
    handleCustomerSelection,
    handleCustomerInput,
    handleSupplierSelection,
    handleSupplierInput,
    handleCreateCustomer,
    handleCreateSupplier,
    handleBankCreateCustomer,
    handleBankCreateSupplier,
    handleAssign,
    handleBankAssign,
    handleDeleteTransaction,
    handleDeleteBankTransaction,
}) {
    if (!hasAccounting) {
        return (
            <Box
                sx={{
                    py: { xs: 6, md: 10 },
                    px: 2,
                    background: 'linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)',
                    minHeight: '100vh',
                }}
            >
                <Container maxWidth="md">
                    <Stack spacing={2} alignItems="flex-start">
                        <Typography variant="h4" fontWeight={800}>
                            Nicht zugeordnete Vorgänge
                        </Typography>
                        <Typography color="text.secondary">
                            Der Zahlungsabgleich ist Teil des Buchhaltungstarifs.
                        </Typography>
                        <UpgradeNotice
                            title="Zahlungsvorgänge freischalten"
                            description="Schalte den Buchhaltungstarif frei, um PayPal-Importe zuzuordnen."
                            actionLabel="Zum Buchhaltungstarif"
                        />
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)',
                    py: { xs: 3, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
                                Nicht zugeordnete Vorgänge
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                                Bankdaten- und PayPal-Importe prüfen und Zahlungen zuordnen.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshRoundedIcon />}
                            onClick={fetchTransactions}
                            disabled={loading || bankLoading}
                        >
                            Aktualisieren
                        </Button>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mt: 3 }}>
                <Stack spacing={3}>
                    <BankCsvImportPanel onImportSuccess={fetchTransactions} />
                    <PayPalImportPanel />
                </Stack>
            </Container>

            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Card variant="outlined">
                    <CardHeader title="Offene Bank-Vorgänge" />
                    <CardContent>
                        {bankError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {bankError}
                            </Alert>
                        )}
                        {bankLoading ? (
                            <Typography color="text.secondary">Lade Bankvorgaenge...</Typography>
                        ) : bankTransactions.length === 0 ? (
                            <Alert severity="info">Keine offenen Bank-Vorgänge gefunden.</Alert>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Buchungsdatum</TableCell>
                                            <TableCell align="right">Betrag</TableCell>
                                            <TableCell>Währung</TableCell>
                                            <TableCell>Gegenpartei</TableCell>
                                            <TableCell>Verwendungszweck</TableCell>
                                            <TableCell>IBAN</TableCell>
                                            <TableCell>Beleg</TableCell>
                                            <TableCell>Aktionen</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bankTransactions.map((tx) => {
                                            const amount = Number(tx.amount ?? 0);
                                            return (
                                                <TableRow key={tx.id}>
                                                    <TableCell>{formatDate(tx.bookingDate || tx.valueDate)}</TableCell>
                                                    <TableCell align="right">
                                                        {formatAmount(amount, tx.currency)}
                                                    </TableCell>
                                                    <TableCell>{tx.currency || '-'}</TableCell>
                                                    <TableCell>{tx.counterpartName || '-'}</TableCell>
                                                    <TableCell>{tx.description || '-'}</TableCell>
                                                    <TableCell>{tx.counterpartIban || '-'}</TableCell>
                                                    <TableCell>{tx.matchedInvoiceNumber || '-'}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Button
                                                                size="small"
                                                                onClick={() => openBankAssignDialog(tx)}
                                                                disabled={deletingBankTransactionId === tx.id}
                                                            >
                                                                Zuordnen
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteBankTransaction(tx.id)}
                                                                disabled={deletingBankTransactionId !== null}
                                                            >
                                                                {deletingBankTransactionId === tx.id ? 'Loeschen...' : 'Löschen'}
                                                            </Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardHeader title="Offene PayPal-Vorgänge" />
                    <CardContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {loading ? (
                            <Typography color="text.secondary">Lade Vorgänge...</Typography>
                        ) : transactions.length === 0 ? (
                            <Alert severity="info">Keine offenen PayPal-Vorgänge gefunden.</Alert>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Datum</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Typ</TableCell>
                                            <TableCell align="right">Netto</TableCell>
                                            <TableCell align="right">Gebuehr</TableCell>
                                            <TableCell>Währung</TableCell>
                                            <TableCell align="right">Offen</TableCell>
                                            <TableCell>Beschreibung</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((tx) => {
                                            const remaining = Number(tx.remainingAmount ?? 0);
                                            const netAmount = Number(tx.netAmount ?? 0);
                                            const feeAmount = Number(tx.feeAmount ?? 0);
                                            return (
                                                <TableRow key={tx.id}>
                                                    <TableCell>{formatDateTime(tx.transactionDateTime)}</TableCell>
                                                    <TableCell>{tx.name || '-'}</TableCell>
                                                    <TableCell>{tx.type || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        {formatAmount(netAmount, tx.currency)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatAmount(feeAmount, tx.currency)}
                                                    </TableCell>
                                                    <TableCell>{tx.currency || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        {formatAmount(remaining, tx.currency)}
                                                    </TableCell>
                                                    <TableCell>{tx.description || '-'}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Button
                                                                size="small"
                                                                onClick={() => openAssignDialog(tx)}
                                                                disabled={deletingTransactionId === tx.id}
                                                            >
                                                                Zuordnen
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteTransaction(tx.id)}
                                                                disabled={deletingTransactionId !== null}
                                                            >
                                                                {deletingTransactionId === tx.id ? 'Loeschen...' : 'Löschen'}
                                                            </Button>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>

            <Dialog open={assignDialogOpen} onClose={closeAssignDialog} maxWidth="md" fullWidth>
                <DialogTitle>PayPal-Vorgang zuordnen</DialogTitle>
                <DialogContent dividers>
                    {activeTransaction && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2">
                                    {activeTransaction.name || 'PayPal'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {`${formatDateTime(activeTransaction.transactionDateTime)} - ${formatAmount(activeTransaction.netAmount, activeTransaction.currency)}`}
                                </Typography>
                            </Box>

                            {assignError && <Alert severity="error">{assignError}</Alert>}

                            {allocationRows.map((row) => {
                                const rowErrors = allocationErrors[row.id] || {};
                                return (
                                <Box key={row.id}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                                        <FormControl size="small" sx={{ minWidth: 180 }}>
                                            <InputLabel id={`alloc-type-${row.id}`}>Ziel</InputLabel>
                                            <Select
                                                labelId={`alloc-type-${row.id}`}
                                                value={row.allocationType}
                                                label="Ziel"
                                                onChange={(event) => handleAllocationTypeChange(row.id, event.target.value)}
                                            >
                                                {ALLOCATION_TYPES.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControl size="small" sx={{ minWidth: 240 }} error={Boolean(rowErrors.targetId)}>
                                            <InputLabel id={`alloc-target-${row.id}`}>Auswahl</InputLabel>
                                            <Select
                                                labelId={`alloc-target-${row.id}`}
                                                value={row.targetId}
                                                label="Auswahl"
                                                disabled={row.allocationType === 'NEW_INCOME' || row.allocationType === 'NEW_EXPENSE'}
                                                onChange={(event) => updateAllocationRow(row.id, {
                                                    targetId: event.target.value,
                                                })}
                                            >
                                                {renderTargetOptions(row.allocationType, referenceData)}
                                            </Select>
                                            {rowErrors.targetId && (
                                                <FormHelperText>{rowErrors.targetId}</FormHelperText>
                                            )}
                                        </FormControl>

                                        <TextField
                                            label="Betrag"
                                            size="small"
                                            value={row.amount}
                                            onChange={(event) => updateAllocationRow(row.id, { amount: event.target.value })}
                                            error={Boolean(rowErrors.amount)}
                                            helperText={rowErrors.amount || ''}
                                            sx={{ width: 140 }}
                                        />

                                        <AppDatePicker
                                            label="Zahlungsdatum"
                                            size="small"
                                            value={row.paymentDate}
                                            onChange={(date) => updateAllocationRow(row.id, {
                                                paymentDate: date,
                                            })}
                                            sx={{ width: 170 }}
                                        />

                                        {allocationRows.length > 1 && (
                                            <Button color="inherit" onClick={() => removeAllocationRow(row.id)}>
                                                Entfernen
                                            </Button>
                                        )}
                                    </Stack>

                                    {(row.allocationType === 'NEW_INCOME' || row.allocationType === 'NEW_EXPENSE') && (
                                        <Box sx={{ mt: 2, pl: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Neue Daten
                                            </Typography>
                                            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                                            <AppDatePicker
                                                    label="Belegdatum"
                                                    size="small"
                                                    value={row.allocationType === 'NEW_INCOME'
                                                        ? row.newIncome.invoiceDate
                                                        : row.newExpense.invoiceDate}
                                                    onChange={(date) => updateAllocationNested(row.id,
                                                        row.allocationType === 'NEW_INCOME' ? 'newIncome' : 'newExpense',
                                                        { invoiceDate: date }
                                                    )}
                                                    error={Boolean(rowErrors.invoiceDate)}
                                                    helperText={rowErrors.invoiceDate || ''}
                                                />
                                                <TextField
                                                    label="Belegnummer/Anzeigename"
                                                    size="small"
                                                    value={row.allocationType === 'NEW_INCOME'
                                                        ? row.newIncome.invoiceNumber
                                                        : row.newExpense.invoiceNumber}
                                                    onChange={(event) => updateAllocationNested(row.id,
                                                        row.allocationType === 'NEW_INCOME' ? 'newIncome' : 'newExpense',
                                                        { invoiceNumber: event.target.value }
                                                    )}
                                                    error={Boolean(rowErrors.invoiceNumber)}
                                                    helperText={rowErrors.invoiceNumber || ''}
                                                />
                                                {row.allocationType === 'NEW_INCOME' ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: { xs: 'column', md: 'row' },
                                                            gap: 1,
                                                            alignItems: { xs: 'stretch', md: 'center' },
                                                            flex: 1,
                                                            minWidth: 240,
                                                        }}
                                                    >
                                                        <Autocomplete
                                                            size="small"
                                                            options={customerOptions}
                                                            value={resolveCustomerOption(
                                                                customerOptions,
                                                                row.newIncome.customerId,
                                                                row.newIncome.customerId ? row.newIncome.customerName : ''
                                                            )}
                                                            inputValue={row.newIncome.customerSearch}
                                                            onChange={(event, value) => handleCustomerSelection(row.id, value)}
                                                            onInputChange={(event, value, reason) =>
                                                                handleCustomerInput(row.id, value, reason)
                                                            }
                                                            getOptionLabel={getCustomerOptionLabel}
                                                            isOptionEqualToValue={isCustomerOptionEqualToValue}
                                                            loading={referenceLoading}
                                                            loadingText={CUSTOMER_SELECT_LOADING}
                                                            noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={CUSTOMER_SELECT_LABEL}
                                                                    placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                                    size="small"
                                                                    error={Boolean(rowErrors.customerName)}
                                                                    helperText={
                                                                        rowErrors.customerName ||
                                                                        (!referenceLoading && customerOptions.length === 0
                                                                            ? CUSTOMER_SELECT_EMPTY_HELPER
                                                                            : '')
                                                                    }
                                                                />
                                                            )}
                                                            sx={{ flex: 1, minWidth: 220 }}
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleCreateCustomer(row.id)}
                                                            disabled={creatingCustomer || !row.newIncome.customerSearch.trim()}
                                                        >
                                                            {creatingCustomer ? 'Anlegen...' : 'Kunde anlegen'}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: { xs: 'column', md: 'row' },
                                                            gap: 1,
                                                            alignItems: { xs: 'stretch', md: 'center' },
                                                            flex: 1,
                                                            minWidth: 240,
                                                        }}
                                                    >
                                                        <Autocomplete
                                                            size="small"
                                                            options={supplierOptions}
                                                            value={resolveSupplierOption(
                                                                supplierOptions,
                                                                row.newExpense.supplierId,
                                                                row.newExpense.supplierId ? row.newExpense.supplierName : ''
                                                            )}
                                                            inputValue={row.newExpense.supplierName}
                                                            onChange={(event, value) => handleSupplierSelection(row.id, value)}
                                                            onInputChange={(event, value, reason) =>
                                                                handleSupplierInput(row.id, value, reason)
                                                            }
                                                            getOptionLabel={getSupplierOptionLabel}
                                                            isOptionEqualToValue={isSupplierOptionEqualToValue}
                                                            loading={referenceLoading}
                                                            loadingText={SUPPLIER_SELECT_LOADING}
                                                            noOptionsText={SUPPLIER_SELECT_NO_OPTIONS}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={SUPPLIER_SELECT_LABEL}
                                                                    placeholder={SUPPLIER_SELECT_PLACEHOLDER}
                                                                    size="small"
                                                                    error={Boolean(rowErrors.supplierName)}
                                                                    helperText={
                                                                        rowErrors.supplierName ||
                                                                        (!referenceLoading && supplierOptions.length === 0
                                                                            ? SUPPLIER_SELECT_EMPTY_HELPER
                                                                            : '')
                                                                    }
                                                                />
                                                            )}
                                                            sx={{ flex: 1, minWidth: 220 }}
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleCreateSupplier(row.id)}
                                                            disabled={creatingSupplier || !row.newExpense.supplierName.trim()}
                                                        >
                                                            {creatingSupplier ? 'Anlegen...' : 'Lieferant anlegen'}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Stack>
                                            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 2 }}>
                                                <TextField
                                                    label="Brutto"
                                                    size="small"
                                                    value={row.allocationType === 'NEW_INCOME'
                                                        ? row.newIncome.grossAmount
                                                        : row.newExpense.grossAmount}
                                                    onChange={(event) => updateAllocationNested(row.id,
                                                        row.allocationType === 'NEW_INCOME' ? 'newIncome' : 'newExpense',
                                                        { grossAmount: event.target.value }
                                                    )}
                                                    error={Boolean(rowErrors.grossAmount)}
                                                    helperText={rowErrors.grossAmount || ''}
                                                />
                                                <TextField
                                                    label="Netto"
                                                    size="small"
                                                    value={row.allocationType === 'NEW_INCOME'
                                                        ? row.newIncome.netAmount
                                                        : row.newExpense.netAmount}
                                                    onChange={(event) => updateAllocationNested(row.id,
                                                        row.allocationType === 'NEW_INCOME' ? 'newIncome' : 'newExpense',
                                                        { netAmount: event.target.value }
                                                    )}
                                                    error={Boolean(rowErrors.netAmount)}
                                                    helperText={rowErrors.netAmount || ''}
                                                />
                                                <TextField
                                                    label="USt"
                                                    size="small"
                                                    value={row.allocationType === 'NEW_INCOME'
                                                        ? row.newIncome.vatAmount
                                                        : row.newExpense.vatAmount}
                                                    onChange={(event) => updateAllocationNested(row.id,
                                                        row.allocationType === 'NEW_INCOME' ? 'newIncome' : 'newExpense',
                                                        { vatAmount: event.target.value }
                                                    )}
                                                    error={Boolean(rowErrors.vatAmount)}
                                                    helperText={rowErrors.vatAmount || ''}
                                                />
                                            </Stack>
                                        </Box>
                                    )}
                                    <Divider sx={{ my: 2 }} />
                                </Box>
                                );
                            })}

                            <Button startIcon={<AddRoundedIcon />} onClick={addAllocationRow}>
                                Zuordnung hinzufuegen
                            </Button>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAssignDialog} disabled={assigning}>
                        Abbrechen
                    </Button>
                    <Button variant="contained" onClick={handleAssign} disabled={assigning}>
                        {assigning ? 'Zuordnung laeuft...' : 'Zuordnen'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={bankAssignDialogOpen} onClose={closeBankAssignDialog} maxWidth="md" fullWidth>
                <DialogTitle>Bank-Vorgang zuordnen</DialogTitle>
                <DialogContent dividers>
                    {activeBankTransaction && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2">
                                    {activeBankTransaction.counterpartName || 'Bank'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {`${formatDate(activeBankTransaction.bookingDate || activeBankTransaction.valueDate)} - ${formatAmount(activeBankTransaction.amount, activeBankTransaction.currency)}`}
                                </Typography>
                            </Box>

                            {bankAssignError && (
                                <Alert severity="error">{bankAssignError}</Alert>
                            )}

                            {referenceLoading ? (
                                <Typography color="text.secondary">Lade Zuordnungsdaten...</Typography>
                            ) : (
                                <Stack spacing={2}>
                                    {bankAllocationRows.map((row) => {
                                        const rowErrors = bankAllocationErrors[row.id] || {};
                                        return (
                                        <Box key={row.id}>
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <InputLabel id={`bank-alloc-type-${row.id}`}>Ziel</InputLabel>
                                                    <Select
                                                        labelId={`bank-alloc-type-${row.id}`}
                                                        value={row.allocationType}
                                                        label="Ziel"
                                                        onChange={(event) =>
                                                            handleBankAllocationTypeChange(row.id, event.target.value)
                                                        }
                                                    >
                                                        {ALLOCATION_TYPES.map((item) => (
                                                            <MenuItem key={item.value} value={item.value}>
                                                                {item.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <FormControl size="small" sx={{ minWidth: 240 }} error={Boolean(rowErrors.targetId)}>
                                                    <InputLabel id={`bank-alloc-target-${row.id}`}>Auswahl</InputLabel>
                                                    <Select
                                                        labelId={`bank-alloc-target-${row.id}`}
                                                        value={row.targetId}
                                                        label="Auswahl"
                                                        disabled={row.allocationType === 'NEW_INCOME'
                                                            || row.allocationType === 'NEW_EXPENSE'}
                                                        onChange={(event) => updateBankAllocationRow(row.id, {
                                                            targetId: event.target.value,
                                                        })}
                                                    >
                                                        {renderTargetOptions(row.allocationType, referenceData)}
                                                    </Select>
                                                    {rowErrors.targetId && (
                                                        <FormHelperText>{rowErrors.targetId}</FormHelperText>
                                                    )}
                                                </FormControl>

                                                <TextField
                                                    label="Betrag"
                                                    size="small"
                                                    value={row.amount}
                                                    onChange={(event) =>
                                                        updateBankAllocationRow(row.id, { amount: event.target.value })
                                                    }
                                                    error={Boolean(rowErrors.amount)}
                                                    helperText={rowErrors.amount || ''}
                                                    sx={{ width: 140 }}
                                                />

                                                <AppDatePicker
                                                    label="Zahlungsdatum"
                                                    size="small"
                                                    value={row.paymentDate}
                                                    onChange={(date) => updateBankAllocationRow(row.id, {
                                                        paymentDate: date,
                                                    })}
                                                    sx={{ width: 170 }}
                                                />

                                                {bankAllocationRows.length > 1 && (
                                                    <Button color="inherit" onClick={() => removeBankAllocationRow(row.id)}>
                                                        Entfernen
                                                    </Button>
                                                )}
                                            </Stack>

                                            {(row.allocationType === 'NEW_INCOME'
                                                || row.allocationType === 'NEW_EXPENSE') && (
                                                <Box sx={{ mt: 2, pl: 1 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Neue Daten
                                                    </Typography>
                                                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                                                    <AppDatePicker
                                                        label="Belegdatum"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.invoiceDate
                                                            : row.newExpense.invoiceDate}
                                                        onChange={(date) => updateBankAllocationNested(row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { invoiceDate: date }
                                                        )}
                                                        error={Boolean(rowErrors.invoiceDate)}
                                                        helperText={rowErrors.invoiceDate || ''}
                                                    />
                                                    <TextField
                                                        label="Belegnummer/Anzeigename"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.invoiceNumber
                                                            : row.newExpense.invoiceNumber}
                                                        onChange={(event) => updateBankAllocationNested(row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { invoiceNumber: event.target.value }
                                                        )}
                                                        error={Boolean(rowErrors.invoiceNumber)}
                                                        helperText={rowErrors.invoiceNumber || ''}
                                                    />
                                                        {row.allocationType === 'NEW_INCOME' ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: { xs: 'column', md: 'row' },
                                                                    gap: 1,
                                                                    alignItems: { xs: 'stretch', md: 'center' },
                                                                    flex: 1,
                                                                    minWidth: 240,
                                                                }}
                                                            >
                                                                <Autocomplete
                                                                    size="small"
                                                                    options={customerOptions}
                                                                    value={resolveCustomerOption(
                                                                        customerOptions,
                                                                        row.newIncome.customerId,
                                                                        row.newIncome.customerId ? row.newIncome.customerName : ''
                                                                    )}
                                                                    inputValue={row.newIncome.customerSearch}
                                                                    onChange={(event, value) =>
                                                                        handleCustomerSelection(
                                                                            row.id,
                                                                            value,
                                                                            updateBankAllocationNested
                                                                        )}
                                                                    onInputChange={(event, value, reason) =>
                                                                        handleCustomerInput(
                                                                            row.id,
                                                                            value,
                                                                            reason,
                                                                            updateBankAllocationNested
                                                                        )}
                                                                    getOptionLabel={getCustomerOptionLabel}
                                                                    isOptionEqualToValue={isCustomerOptionEqualToValue}
                                                                    loading={referenceLoading}
                                                                    loadingText={CUSTOMER_SELECT_LOADING}
                                                                    noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label={CUSTOMER_SELECT_LABEL}
                                                                            placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                                            size="small"
                                                                            error={Boolean(rowErrors.customerName)}
                                                                            helperText={
                                                                                rowErrors.customerName ||
                                                                                (!referenceLoading && customerOptions.length === 0
                                                                                    ? CUSTOMER_SELECT_EMPTY_HELPER
                                                                                    : '')
                                                                            }
                                                                        />
                                                                    )}
                                                                    sx={{ flex: 1, minWidth: 220 }}
                                                                />
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => handleBankCreateCustomer(row.id)}
                                                                    disabled={bankCreatingCustomer
                                                                        || !row.newIncome.customerSearch.trim()}
                                                                >
                                                                    {bankCreatingCustomer ? 'Anlegen...' : 'Kunde anlegen'}
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: { xs: 'column', md: 'row' },
                                                                    gap: 1,
                                                                    alignItems: { xs: 'stretch', md: 'center' },
                                                                    flex: 1,
                                                                    minWidth: 240,
                                                                }}
                                                            >
                                                            <Autocomplete
                                                                size="small"
                                                                options={supplierOptions}
                                                                value={resolveSupplierOption(
                                                                    supplierOptions,
                                                                    row.newExpense.supplierId,
                                                                    row.newExpense.supplierId ? row.newExpense.supplierName : ''
                                                                )}
                                                                inputValue={row.newExpense.supplierName}
                                                                onChange={(event, value) =>
                                                                    handleSupplierSelection(
                                                                        row.id,
                                                                        value,
                                                                        updateBankAllocationNested
                                                                    )}
                                                                onInputChange={(event, value, reason) =>
                                                                    handleSupplierInput(
                                                                        row.id,
                                                                        value,
                                                                        reason,
                                                                        updateBankAllocationNested
                                                                    )}
                                                                getOptionLabel={getSupplierOptionLabel}
                                                                isOptionEqualToValue={isSupplierOptionEqualToValue}
                                                                loading={referenceLoading}
                                                                loadingText={SUPPLIER_SELECT_LOADING}
                                                                noOptionsText={SUPPLIER_SELECT_NO_OPTIONS}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={SUPPLIER_SELECT_LABEL}
                                                                        placeholder={SUPPLIER_SELECT_PLACEHOLDER}
                                                                        size="small"
                                                                        error={Boolean(rowErrors.supplierName)}
                                                                        helperText={
                                                                            rowErrors.supplierName ||
                                                                            (!referenceLoading && supplierOptions.length === 0
                                                                                ? SUPPLIER_SELECT_EMPTY_HELPER
                                                                                : '')
                                                                        }
                                                                    />
                                                                )}
                                                                sx={{ flex: 1, minWidth: 220 }}
                                                            />
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => handleBankCreateSupplier(row.id)}
                                                                    disabled={bankCreatingSupplier
                                                                        || !row.newExpense.supplierName.trim()}
                                                                >
                                                                    {bankCreatingSupplier ? 'Anlegen...' : 'Lieferant anlegen'}
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 2 }}>
                                                        <TextField
                                                            label="Brutto"
                                                            size="small"
                                                            value={row.allocationType === 'NEW_INCOME'
                                                                ? row.newIncome.grossAmount
                                                                : row.newExpense.grossAmount}
                                                            onChange={(event) => updateBankAllocationNested(row.id,
                                                                row.allocationType === 'NEW_INCOME'
                                                                    ? 'newIncome'
                                                                    : 'newExpense',
                                                                { grossAmount: event.target.value }
                                                            )}
                                                            error={Boolean(rowErrors.grossAmount)}
                                                            helperText={rowErrors.grossAmount || ''}
                                                        />
                                                        <TextField
                                                            label="Netto"
                                                            size="small"
                                                            value={row.allocationType === 'NEW_INCOME'
                                                                ? row.newIncome.netAmount
                                                                : row.newExpense.netAmount}
                                                            onChange={(event) => updateBankAllocationNested(row.id,
                                                                row.allocationType === 'NEW_INCOME'
                                                                    ? 'newIncome'
                                                                    : 'newExpense',
                                                                { netAmount: event.target.value }
                                                            )}
                                                            error={Boolean(rowErrors.netAmount)}
                                                            helperText={rowErrors.netAmount || ''}
                                                        />
                                                        <TextField
                                                            label="USt"
                                                            size="small"
                                                            value={row.allocationType === 'NEW_INCOME'
                                                                ? row.newIncome.vatAmount
                                                                : row.newExpense.vatAmount}
                                                            onChange={(event) => updateBankAllocationNested(row.id,
                                                                row.allocationType === 'NEW_INCOME'
                                                                    ? 'newIncome'
                                                                    : 'newExpense',
                                                                { vatAmount: event.target.value }
                                                            )}
                                                            error={Boolean(rowErrors.vatAmount)}
                                                            helperText={rowErrors.vatAmount || ''}
                                                        />
                                                    </Stack>
                                                </Box>
                                            )}

                                            <Divider sx={{ my: 2 }} />
                                        </Box>
                                        );
                                    })}
                                    <TextField
                                        label="Kommentar"
                                        size="small"
                                        multiline
                                        minRows={2}
                                        value={bankComment}
                                        onChange={(event) => setBankComment(event.target.value)}
                                    />
                                    <Button startIcon={<AddRoundedIcon />} onClick={addBankAllocationRow}>
                                        Zuordnung hinzufuegen
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeBankAssignDialog} disabled={bankAssigning}>
                        Abbrechen
                    </Button>
                    <Button variant="contained" onClick={handleBankAssign} disabled={bankAssigning}>
                        {bankAssigning ? 'Zuordnung laeuft...' : 'Zuordnen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
